/**
 * web/src/components/shared/Textarea.tsx
 * Textarea matching `.textarea` (design_files/styles.css). Same border/focus
 * pattern as Input, but min-height 80px (overridable) and `resize: vertical`.
 * Used by RejectModal and the email body textarea (which uses `email-body`
 * overrides — see EmailPreview in Plan 06).
 */
import { forwardRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** Public props for Textarea — inherits every native textarea attribute. */
export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * Hand-rolled Textarea primitive.
 * Side effects: none — pure styled wrapper. `resize-y` allows vertical
 * resize per the handoff; consumers can override min-h via className.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'w-full rounded-md border bg-bg px-2.5 py-2 text-[13px] text-text outline-none transition-colors duration-100',
        'border-border-strong placeholder:text-text-faint resize-y min-h-20',
        'focus:border-accent focus:ring-[3px] focus:ring-accent-soft',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';
